/**
 * EKO Commercial — buy-box bag offer (blocks/eko-bag-offer.liquid)
 *
 * Pack-size pills toggle the visible option/price row and swap the hidden
 * form's variant id. When the checkbox is ticked and the MAIN product form
 * completes a successful add (the theme's `shopify:cart:lines-update`
 * event bubbles to the document carrying a resolution promise), the block
 * submits its own hidden Horizon product-form for the selected bag — the
 * second add rides the theme's native event/refresh/drawer contract.
 * Adds that originate inside the block are ignored via event target.
 *
 * <eko-bag-offer> is a custom element so a variant change on a
 * multi-variant product (product-form.js re-morphs [data-eko-variant-slot])
 * can add, remove or rewrite the offer: pills are handled by delegation and
 * every DOM lookup happens at use time, so morphed children just work.
 */

const EVENT_NAME = 'shopify:cart:lines-update';

class EkoBagOffer extends HTMLElement {
  connectedCallback() {
    this.busy = false;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener(
      'click',
      (event) => {
        const pill = event.target instanceof Element ? event.target.closest('[data-offer-pill]') : null;
        if (pill && this.contains(pill)) this.select(Number(pill.dataset.index));
      },
      { signal }
    );

    document.addEventListener(EVENT_NAME, (event) => this.onCartAdd(event), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  select(index) {
    this.querySelectorAll('[data-offer-pill]').forEach((p, i) => {
      p.classList.toggle('is-selected', i === index);
      p.setAttribute('aria-pressed', i === index ? 'true' : 'false');
    });
    const options = [...this.querySelectorAll('[data-offer-option]')];
    options.forEach((o, i) => o.classList.toggle('is-selected', i === index));
    this.querySelectorAll('[data-offer-price]').forEach((o, i) => o.classList.toggle('is-selected', i === index));
    const selected = options[index];
    const variantInput = this.querySelector('[data-offer-variant-input]');
    if (selected && variantInput) {
      variantInput.value = selected.dataset.variantId;
    }
  }

  onCartAdd(event) {
    const check = this.querySelector('[data-offer-check]');
    if (!this.isConnected || !check || !check.checked || this.busy) return;
    // Ignore adds we triggered ourselves (and any cart-drawer activity).
    if (event.target instanceof Element && event.target.closest('[data-eko-bag-offer]')) return;
    // Only the MAIN buy-box add for THIS product may trigger the bundle —
    // other product-form adds (e.g. the cart drawer's own upsell) must not.
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.mainProductId) {
      return;
    }
    // ShopifyStandardEvent spreads its payload onto the event object itself
    // (action/context/promise are NOT under event.detail).
    if (event.action !== 'add' || event.context !== 'product') return;
    if (!event.promise || typeof event.promise.then !== 'function') return;
    this.busy = true;
    event.promise
      .then((result) => {
        if (result && result.detail && result.detail.didError) return;
        this.querySelector('.eko-bag-offer__hidden-add')?.click();
      })
      .catch(() => {})
      .finally(() => {
        this.busy = false;
      });
  }
}

if (!customElements.get('eko-bag-offer')) {
  customElements.define('eko-bag-offer', EkoBagOffer);
}
